import {
    Component,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    LOCALE_ID,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { CourseListingDetailModel } from '../../../../models/webinar-course.model';
import {
    COURSE_DATE_FORMAT,
    GENERAL_IMAGE_PLACEHOLDER,
    DAYS,
    MINUTES,
    PUBLISHED_DATE_FORMAT,
    SCREEN_WIDTH_DESKTOP,
    TWELVE_HOURS_FORMAT,
} from '../../../../constants/app.constant';
import { ConfirmationPopupInterface } from '../confirmation-popup/confirmation-popup.model';
import { filter, finalize, Observable, switchMap, tap } from 'rxjs';
import { BlockUiService } from '../block-ui/block-ui.service';
import { ProgramService } from '../../../../services/api/program.service';
import { ErrorService } from '../../../../services/error.service';
import { getUiProgramTag } from '../../../../constants/webinar-course.constant';
import { getLongLanguageOptions, Language } from '../../../../constants/ui-utils.constant';
import { CountdownConfig } from 'ngx-countdown';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { LanguageLocal, UiProgramType } from '../../../../enum/ui-utils.enum';
import { DialogService } from '../../../../services/dialog.service';
import { StorageService } from '../../../../services/storage.service';
import { ProgramType, WebinarType } from '../../../../enum/webinar-course.enum';

@Component({
    selector: 'app-program-card',
    templateUrl: './program-card.component.html',
    styleUrls: ['./program-card.component.scss'],
})
export class ProgramCardComponent implements OnInit, OnDestroy {
    @Output() favouriteRemoveBookmark = new EventEmitter<string>();
    @Output() closeMobilePopup = new EventEmitter();
    @Input() courseListing: CourseListingDetailModel = {} as CourseListingDetailModel;
    @Input() isShowingGridView: boolean = true;
    @Input() isFavourite: boolean = false;
    @Input() isShowProgressBar: boolean = false;
    @Input() isCompleted: boolean = false;
    @Input() isShowTopic: boolean = false;
    @Input() isShowSpeaker: boolean = true;
    @Input() isShowTagging: boolean = true;

    isMobileView = false;
    isShowProgramLiveStatus = false;
    isIllegibleForCertificate = false;
    buttonUi: ButtonUi = {} as ButtonUi;
    topicUi: string = '';
    categories = '';

    UiProgramType = UiProgramType;

    COURSE_DATE_FORMAT = COURSE_DATE_FORMAT;
    TWELVE_HOURS_FORMAT = TWELVE_HOURS_FORMAT;
    GENERAL_IMAGE_PLACEHOLDER = GENERAL_IMAGE_PLACEHOLDER;
    PUBLISHED_DATE_FORMAT = PUBLISHED_DATE_FORMAT;

    getUiProgramTag = getUiProgramTag;
    getLongLanguageOptions = getLongLanguageOptions;

    protected config: CountdownConfig = {};

    private checkIfProgramLiveInterval!: any;
    private isDestroyed: boolean = false;

    constructor(
        @Inject(LOCALE_ID) private locale: string,
        private blockUiService: BlockUiService,
        private dialogService: DialogService,
        private errorService: ErrorService,
        private programService: ProgramService,
        private translate: TranslateService,
        private storageService: StorageService,
    ) {}

    @HostListener('window:resize', ['$event'])
    onResize(): void {
        this.checkScreenSize();
    }

    ngOnInit(): void {
        if (!this.courseListing.isInProgress && this.courseListing.isProgramJoinable && this.courseListing.uiProgramType === UiProgramType.LiveWebinar) {
            this.checkIfProgramIsLive();
        }

        this.checkIfIllegibleForCertificate();
        this.initCounterConfig();
        this.checkScreenSize();
        this.getTopicUi();
        this.checkProgramLiveStatusUi();
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;

        this.clearCheckIfProgramLiveInterval();
    }

    protected checkScreenSize(): void {
        if (window.innerWidth < SCREEN_WIDTH_DESKTOP) {
            this.isMobileView = true;
        } else {
            this.isMobileView = false;
        }
    }

    protected updateBookmarkUi(): void {
        this.courseListing.isFavorite = !this.courseListing.isFavorite;
    }

    /**
     * Returns the effective published date for display.
     * Uses publishedDate.
     * @returns Date object or null if neither date is available
     */
    protected getPublishedDate(): Date | null {
        if (!this.courseListing.publishedDate) {
            return null;
        }
        return new Date(this.courseListing.publishedDate);
    }

    protected onRemoveBookmark(id: string): void {
        const confirmationBody: ConfirmationPopupInterface = {
            title: 'confirmation.removeBookmark',
            message: 'confirmation.removeBookmarkDescription',
            confirmText: 'button.remove',
            cancelText: 'button.cancel',
            icon: 'bg-exclamation-icon',
            isDelete: true,
        };

        this.dialogService
            .showConfirmationPopup(confirmationBody)
            .pipe(
                filter((isConfirm) => isConfirm),
                switchMap(() => {
                    this.blockUiService.open();

                    return this.onRemoveBookmark$(id);
                }),
                finalize(() => this.blockUiService.close()),
            )
            .subscribe();
    }

    protected closeMobilePopupCard(): void {
        this.closeMobilePopup.emit();
    }

    protected showCardActionButton(): boolean {
        return !(this.courseListing.uiProgramType === UiProgramType.LiveWebinar && this.courseListing.isProgramExpired);
    }

    private getTopicUi(): void {
        if (this.courseListing.category) {
            this.topicUi = this.courseListing.category[0];

            if (this.storageService.language === LanguageLocal.LanguageCN) {
                const translation = this.courseListing.categoryTranslations?.find((t) => t.language === Language.CH);

                this.categories = translation?.description?.join(', ') || this.courseListing.category.join(', ');
            } else {
                this.categories = this.courseListing.category.join(', ');
            }
        }
    }

    private checkProgramLiveStatusUi(): void {
        this.isShowProgramLiveStatus =
            !this.courseListing.isProgramExpired &&
            this.courseListing.uiProgramType === UiProgramType.LiveWebinar &&
            this.courseListing.liveDaysCountdown <= 14 &&
            !this.courseListing.isMeetingEnded;
    }

    private onRemoveBookmark$(id: string): Observable<boolean> {
        return this.programService.bookmarkProgram(id, false).pipe(
            tap({
                next: () => {
                    this.favouriteRemoveBookmark.emit(id);
                },
                error: (err) => {
                    this.errorService.showError(err);
                },
            }),
        );
    }

    private initCounterConfig(): void {
        if (this.courseListing.liveSecondsCountdown <= 0) {
            return;
        }

        this.config = {
            leftTime: this.courseListing.liveSecondsCountdown,
            notify: [1],
            formatDate: ({ date, formatStr, timezone }) => {
                let f = formatStr;
                let translatedString = '';

                if (date > DAYS) {
                    f = 'd';
                    const roundedDays = Math.round(date / DAYS);
                    translatedString = this.translate.instant('timeFormat.days', { days: roundedDays });
                } else if (date > MINUTES) {
                    f = 'hh:mm';
                    const hours = Math.floor(date / (1000 * 60 * 60))
                        .toString()
                        .padStart(2, '0');
                    const minutes = Math.floor((date % (1000 * 60 * 60)) / (1000 * 60))
                        .toString()
                        .padStart(2, '0');
                    translatedString = this.translate.instant('timeFormat.hoursMinutes', { hours, minutes });
                } else {
                    translatedString = this.translate.instant('timeFormat.lessThanOneMinute');
                }

                if (date <= 0) {
                    this.courseListing.isProgramJoinable = true;

                    this.checkIfProgramIsLive();
                }

                return translatedString || formatDate(date, f, this.locale, timezone || '+0000');
            },
        };
    }

    private checkIfProgramIsLive(): void {
        const checkIsLiveDelay = 15000;

        if (this.courseListing.isInProgress) {
            this.clearCheckIfProgramLiveInterval();
            return;
        }

        this.checkIfProgramLiveInterval = setInterval(() => {
            this.clearCheckIfProgramLiveInterval();
            
            if (this.courseListing.isProgramJoinable && !this.courseListing.isInProgress) {
                this.getProfileLiveDetails();
            }
        }, checkIsLiveDelay);
    }

    private clearCheckIfProgramLiveInterval(){
        if(this.checkIfProgramLiveInterval){
            clearInterval(this.checkIfProgramLiveInterval);
        }
    }

    private getProfileLiveDetails(): void {
        this.programService.getProgramInProgressStatus(this.courseListing.id).subscribe({
            next: (resp) => {
                if (resp.result) {
                    this.courseListing.isInProgress = true;
                } else {
                    if(this.isDestroyed){
                        return;
                    }

                    this.checkIfProgramIsLive();
                }
            },
            error: (error) => {
                if(error.message == 'error.multipleDeviceLoginDetected' || error.message == 'error.sessionExpired'){
                    this.errorService.showError(error);
                }
            },
        });
    }

    private checkIfIllegibleForCertificate(): void {
        const isLiveWebinar =
            this.courseListing.programType === ProgramType.Webinar &&
            this.courseListing.webinarType === WebinarType.Live;
        const isCompleted = !!this.courseListing.completedAt;
        const certificateDisabled = !this.courseListing.isCertificateEnabled;
        const progressTooLow = this.courseListing.progress < 80;

        this.isIllegibleForCertificate = isLiveWebinar && isCompleted && (certificateDisabled || progressTooLow);
    }
}

interface ButtonUi {
    icon: string;
    text: string;
}
